import { useCallback } from 'react'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useSentry from 'Pods/Shared/Hooks/useSentry'

/**
 * Logging utilities for Use Case actions
 * Provides consistent Sentry error instrumentation across use case operations
 */

const buildUseCaseLogContext = (action, pageConfig, role, currentUsername, data = {}) => {
  return {
    action,
    itemId: pageConfig?.product?.id,
    role,
    username: currentUsername,
    ...data,
  }
}

const logUseCaseError = (sentry, action, error, context) => {
  if (!sentry?.captureException) return

  sentry.captureException(error, {
    tags: {
      component: 'use_case',
      action,
    },
    contexts: {
      use_case: context,
    },
  })
}

/**
 * Hook to manage Use Case error logging with consistent context
 * @param {string} role - User role (optional)
 * @param {string} currentUsername - Current username (optional)
 * @returns {Function} logError function
 */
export default function useUseCaseLogger(role = null, currentUsername = null) {
  const pageConfig = usePageConfig()
  const sentry = useSentry()

  const logError = useCallback((action, error, data = {}) => {
    const context = buildUseCaseLogContext(action, pageConfig, role, currentUsername, data)
    logUseCaseError(sentry, action, error, context)
  }, [sentry, pageConfig, role, currentUsername])

  return { logError }
}
